import { useState, useEffect, useRef } from 'react'
import {
  Mail,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Server,
  Cloud,
  Zap,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'
import { useMailboxes, useDeleteMailbox, useResetDailyCount } from '@hooks/useMailboxes'
import { Mailbox } from '@/types'
import { formatDateTime, formatNumber } from '@lib/utils'
import { MailboxFilters } from '@services/mailbox.service'

interface MailboxListProps {
  onEdit: (mailbox: Mailbox) => void
}

export function MailboxList({ onEdit }: MailboxListProps) {
  const [filters, setFilters] = useState<MailboxFilters>({
    page: 1,
    pageSize: 10,
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, error } = useMailboxes(filters)
  const deleteMutation = useDeleteMailbox()
  const resetCountMutation = useResetDailyCount()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters({ ...filters, search: searchTerm, page: 1 })
  }

  const handleStatusFilter = (status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'ERROR' | undefined) => {
    setFilters({ ...filters, status, page: 1 })
  }

  const handleProviderFilter = (provider: 'AWS_SES' | 'SMTP' | 'SENDGRID' | undefined) => {
    setFilters({ ...filters, provider, page: 1 })
  }

  const handleHealthFilter = (
    healthStatus: 'HEALTHY' | 'WARNING' | 'ERROR' | 'UNKNOWN' | undefined
  ) => {
    setFilters({ ...filters, healthStatus, page: 1 })
  }

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this mailbox?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleResetCount = async (id: number) => {
    if (confirm('Reset daily send count for this mailbox?')) {
      await resetCountMutation.mutateAsync(id)
    }
  }

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-6 text-center">
        <p className="text-rose-400">Failed to load mailboxes</p>
      </div>
    )
  }

  const mailboxes = data?.data || []
  const pagination = data?.pagination

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            className="input-modern pl-10"
          />
        </div>
        <button
          type="submit"
          className="btn-primary"
        >
          Search
        </button>
      </form>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        {/* Status Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-slate-400">Status:</span>
          <button
            onClick={() => handleStatusFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === undefined
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleStatusFilter('ACTIVE')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'ACTIVE'
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => handleStatusFilter('INACTIVE')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'INACTIVE'
                ? 'bg-slate-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Inactive
          </button>
          <button
            onClick={() => handleStatusFilter('SUSPENDED')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'SUSPENDED'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Suspended
          </button>
          <button
            onClick={() => handleStatusFilter('ERROR')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.status === 'ERROR'
                ? 'bg-rose-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Error
          </button>
        </div>

        {/* Provider Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-slate-400">Provider:</span>
          <button
            onClick={() => handleProviderFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.provider === undefined
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleProviderFilter('AWS_SES')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.provider === 'AWS_SES'
                ? 'bg-orange-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            AWS SES
          </button>
          <button
            onClick={() => handleProviderFilter('SMTP')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.provider === 'SMTP'
                ? 'bg-blue-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            SMTP
          </button>
          <button
            onClick={() => handleProviderFilter('SENDGRID')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.provider === 'SENDGRID'
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            SendGrid
          </button>
        </div>

        {/* Health Status Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-slate-400">Health:</span>
          <button
            onClick={() => handleHealthFilter(undefined)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.healthStatus === undefined
                ? 'bg-cyan-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleHealthFilter('HEALTHY')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.healthStatus === 'HEALTHY'
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Healthy
          </button>
          <button
            onClick={() => handleHealthFilter('WARNING')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.healthStatus === 'WARNING'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Warning
          </button>
          <button
            onClick={() => handleHealthFilter('ERROR')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              filters.healthStatus === 'ERROR'
                ? 'bg-rose-500 text-white'
                : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Error
          </button>
        </div>
      </div>

      {/* Mailbox List */}
      {mailboxes.length === 0 ? (
        <div className="flex flex-col items-center justify-center stat-card p-12">
          <Mail className="mb-4 h-16 w-16 text-slate-600" />
          <h3 className="text-lg font-semibold text-slate-200">No mailboxes found</h3>
          <p className="mt-2 text-sm text-slate-500">
            {filters.search || filters.status || filters.provider || filters.healthStatus
              ? 'Try adjusting your filters'
              : 'Add your first mailbox to start sending emails'}
          </p>
        </div>
      ) : (
        <div className="stat-card p-0">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50 border-b border-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Mailbox
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Daily Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-400">
                  Last Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {mailboxes.map((mailbox) => (
                <MailboxRow
                  key={mailbox.id}
                  mailbox={mailbox}
                  onEdit={() => onEdit(mailbox)}
                  onDelete={() => handleDelete(mailbox.id)}
                  onResetCount={() => handleResetCount(mailbox.id)}
                />
              ))}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-700/50 px-6 py-4">
              <p className="text-sm text-slate-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
                  disabled={pagination.page === 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface MailboxRowProps {
  mailbox: Mailbox;
  onEdit: () => void;
  onDelete: () => void;
  onResetCount: () => void;
}

function MailboxRow({ mailbox, onEdit, onDelete, onResetCount }: MailboxRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const usagePercentage = (mailbox.sentToday / mailbox.dailyLimit) * 100

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  // Provider badge
  const getProviderBadge = () => {
    switch (mailbox.provider) {
      case 'AWS_SES':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 px-3 py-1 text-xs font-medium text-orange-400 border border-orange-500/20">
            <Cloud size={14} />
            AWS SES
          </span>
        )
      case 'SMTP':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 px-3 py-1 text-xs font-medium text-blue-400 border border-blue-500/20">
            <Server size={14} />
            SMTP
          </span>
        )
      case 'SENDGRID':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/15 px-3 py-1 text-xs font-medium text-cyan-400 border border-cyan-500/20">
            <Zap size={14} />
            SendGrid
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-500/15 px-3 py-1 text-xs font-medium text-slate-400 border border-slate-500/20">
            <Server size={14} />
            Unknown
          </span>
        )
    }
  }

  // Status badge
  const getStatusBadge = () => {
    switch (mailbox.status) {
      case 'ACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-400 border border-emerald-500/20">
            <CheckCircle size={14} />
            Active
          </span>
        )
      case 'INACTIVE':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-500/15 px-3 py-1 text-xs font-medium text-slate-400 border border-slate-500/20">
            <XCircle size={14} />
            Inactive
          </span>
        )
      case 'SUSPENDED':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-400 border border-amber-500/20">
            <AlertTriangle size={14} />
            Suspended
          </span>
        )
      case 'ERROR':
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-3 py-1 text-xs font-medium text-rose-400 border border-rose-500/20">
            <AlertCircle size={14} />
            Error
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-500/15 px-3 py-1 text-xs font-medium text-slate-400 border border-slate-500/20">
            Unknown
          </span>
        )
    }
  }

  // Health badge
  const getHealthBadge = () => {
    switch (mailbox.healthStatus) {
      case 'HEALTHY':
        return (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-400 border border-emerald-500/20">
              <CheckCircle size={14} />
              Healthy
            </span>
            <div className="mt-1 text-xs text-slate-500">
              Bounce: {Number(mailbox.bounceRate ?? 0).toFixed(2)}% | Complaints: {Number(mailbox.complaintRate ?? 0).toFixed(2)}%
            </div>
          </div>
        )
      case 'WARNING':
        return (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-400 border border-amber-500/20">
              <AlertTriangle size={14} />
              Warning
            </span>
            <div className="mt-1 text-xs text-slate-500">
              Bounce: {Number(mailbox.bounceRate ?? 0).toFixed(2)}% | Complaints: {Number(mailbox.complaintRate ?? 0).toFixed(2)}%
            </div>
          </div>
        )
      case 'ERROR':
        return (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-3 py-1 text-xs font-medium text-rose-400 border border-rose-500/20">
              <AlertCircle size={14} />
              Error
            </span>
            <div className="mt-1 text-xs text-slate-500">
              Bounce: {Number(mailbox.bounceRate ?? 0).toFixed(2)}% | Complaints: {Number(mailbox.complaintRate ?? 0).toFixed(2)}%
            </div>
          </div>
        )
      case 'UNKNOWN':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-500/15 px-3 py-1 text-xs font-medium text-slate-400 border border-slate-500/20">
            <AlertCircle size={14} />
            Unknown
          </span>
        )
    }
  }

  return (
    <tr className="hover:bg-slate-800/30 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center">
          <Mail className="mr-3 h-5 w-5 text-slate-500" />
          <div>
            <div className="font-medium text-slate-100">{mailbox.name}</div>
            <div className="text-sm text-slate-500">{mailbox.email}</div>
            {mailbox.fromName && (
              <div className="text-xs text-slate-600">From: {mailbox.fromName}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4">{getProviderBadge()}</td>
      <td className="px-6 py-4">{getStatusBadge()}</td>
      <td className="px-6 py-4">{getHealthBadge()}</td>
      <td className="px-6 py-4">
        <div className="w-32">
          <div className="mb-1 flex justify-between text-xs">
            <span className="font-medium text-slate-300">
              {formatNumber(mailbox.sentToday)} / {formatNumber(mailbox.dailyLimit)}
            </span>
            <span className="text-slate-500">{usagePercentage.toFixed(0)}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-700">
            <div
              className={`h-full transition-all ${usagePercentage >= 90 ? 'bg-rose-500' : usagePercentage >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-slate-500">
        {mailbox.lastUsedAt ? formatDateTime(mailbox.lastUsedAt) : 'Never'}
      </td>
      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="relative inline-block" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/20 overflow-hidden">
              <button
                onClick={() => {
                  onEdit()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <Edit size={16} className="text-cyan-400" /> Edit Mailbox
              </button>
              <button
                onClick={() => {
                  onResetCount()
                  setShowMenu(false)
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700 hover:text-slate-100 transition-colors"
              >
                <RefreshCw size={16} className="text-amber-400" /> Reset Count
              </button>
              <div className="border-t border-slate-700">
                <button
                  onClick={() => {
                    onDelete()
                    setShowMenu(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 stat-card">
          <div className="h-12 w-12 skeleton rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 skeleton rounded" />
            <div className="h-3 w-1/4 skeleton rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
